import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
    FiPhone,
    FiMail,
    FiClock,
    FiMapPin,
    FiSend,
    FiCheck
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import SectionHeading from '../components/SectionHeading';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';

const Contact = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        companyName: '',
        email: '',
        phone: '',
        service: '',
        propertyType: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate form submission
        await new Promise(resolve => setTimeout(resolve, 1200));

        setSubmitted(true);
        toast.success(t('contactPage.form.success'));
        setIsSubmitting(false);

        // Reset after 3 seconds
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', companyName: '', email: '', phone: '', service: '', propertyType: '', message: '' });
        }, 3000);
    };

    const contactInfo = [
        {
            icon: FiPhone,
            title: t('contactPage.info.call'),
            value: '+973 1770 1616',
            href: 'tel:+97317701616',
            color: 'bg-primary-500',
        },
        {
            icon: FaWhatsapp,
            title: 'WhatsApp',
            value: '+973 6675 2929',
            href: 'https://wa.me/97366752929',
            color: 'bg-green-500',
        },
        {
            icon: FiMail,
            title: t('contactPage.info.email'),
            value: 'info@primeforceservices.com',
            
            href: 'mailto:info@primeforceservices.com',
            color: 'bg-accent-500',
        },
        {
            icon: FiClock,
            title: t('contactPage.info.hours'),
            value: t('contactPage.info.hoursValue'),
            href: null,
            color: 'bg-success-500',
        },
        {
            icon: FiMapPin,
            title: t('contactPage.info.location'),
            value: t('contactPage.info.locationValue'),
            href: 'https://www.google.com/maps/place/26%C2%B013\'52.2%22N+50%C2%B035\'10.8%22E/@26.2311726,50.586338,17z',
            color: 'bg-primary-600',
        },
    ];

    const services = [
        t('common.services.Commercial Cleaning'),
        t('common.services.Residential Cleaning'),
        t('common.services.Industrial Cleaning'),
        t('common.services.Rope Access Window Cleaning'),
        t('common.services.Floor Care & Marble Polishing'),
        t('common.services.Carpet & Upholstery Cleaning'),
        t('common.services.Pest Control & Disinfection'),
        t('common.services.Facility Support Services'),
        t('common.services.Other'),
    ];

    const propertyTypes = [
        { key: 'commercial', label: t('contactPage.form.propertyTypes.commercial') },
        { key: 'residential', label: t('contactPage.form.propertyTypes.residential') },
        { key: 'industrial', label: t('contactPage.form.propertyTypes.industrial') },
        { key: 'institutional', label: t('contactPage.form.propertyTypes.institutional') },
        { key: 'other', label: t('contactPage.form.propertyTypes.other') },
    ];

    return (
        <div className="w-full">
            <SEO
                title={t('seo.contact.title')}
                description={t('seo.contact.description')}
                keywords="contact primeforce, cleaning quote bahrain, facility services quote"
            />

            {/* Hero Banner */}
            <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="/images/site/hero-facility-team.png"
                        alt="Contact PrimeForce Services"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/85"></div>
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-block px-4 py-1.5 bg-accent-500/20 text-accent-300 rounded-full text-xs sm:text-sm font-semibold mb-4 border border-accent-500/30 backdrop-blur-sm"
                    >
                        {t('contactPage.hero.badge')}
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                    >
                        {t('contactPage.hero.titleStart')} <span className="text-accent-400">{t('contactPage.hero.titleAccent')}</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-sm sm:text-base md:text-lg text-gray-200 max-w-3xl mx-auto leading-relaxed"
                    >
                        {t('contactPage.intro')}
                    </motion.p>
                </div>
            </section>

            {/* Contact Info Cards */}
            <section className="relative -mt-12 z-20">
                <div className="container-custom">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
                        {contactInfo.map((info, index) => (
                            <motion.div
                                key={info.title}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.08 }}
                                className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 group border border-gray-100 flex flex-col justify-between h-full"
                            >
                                <div>
                                    <div className={`w-11 h-11 ${info.color} rounded-xl flex items-center justify-center mb-3.5 group-hover:scale-110 transition-transform shadow-md flex-shrink-0`}>
                                        <info.icon className="text-white" size={20} />
                                    </div>
                                    <h3 className="text-sm font-bold text-gray-800 mb-1">{info.title}</h3>
                                    {info.href ? (
                                        <a
                                            href={info.href}
                                            target={info.href.startsWith('http') ? '_blank' : undefined}
                                            rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                            className="text-gray-600 hover:text-primary-600 transition-colors text-xs font-semibold block leading-relaxed break-all"
                                        >
                                            {info.value}
                                        </a>
                                    ) : (
                                        <p className="text-gray-600 text-xs font-medium leading-relaxed">{info.value}</p>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <SectionHeading
                                title={t('contactPage.form.title')}
                                subtitle={t('contactPage.form.subtitle')}
                                description={t('contactPage.form.desc')}
                                centered={false}
                            />

                            <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('contactPage.form.name')}
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="input-primary"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('contactPage.form.companyName')}
                                        </label>
                                        <input
                                            type="text"
                                            id="companyName"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            className="input-primary"
                                            placeholder="Company Name (Optional)"
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('contactPage.form.email')}
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="input-primary"
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('contactPage.form.phone')}
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                            className="input-primary"
                                            placeholder="+973 1234 5678"
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('contactPage.form.service')}
                                        </label>
                                        <select
                                            id="service"
                                            name="service"
                                            value={formData.service}
                                            onChange={handleChange}
                                            required
                                            className="input-primary"
                                        >
                                            <option value="">{t('contactPage.form.selectService')}</option>
                                            {services.map((service) => (
                                                <option key={service} value={service}>{service}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('contactPage.form.propertyType')}
                                        </label>
                                        <select
                                            id="propertyType"
                                            name="propertyType"
                                            value={formData.propertyType}
                                            onChange={handleChange}
                                            className="input-primary"
                                        >
                                            <option value="">{t('contactPage.form.selectPropertyType')}</option>
                                            {propertyTypes.map((pt) => (
                                                <option key={pt.key} value={pt.label}>{pt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('contactPage.form.message')}
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows={4}
                                        className="input-primary resize-none"
                                        placeholder={t('contactPage.form.messagePlaceholder')}
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting || submitted}
                                    className={`btn-accent w-full text-lg py-4 ${submitted ? 'bg-success-500 hover:bg-success-500' : ''}`}
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('contactPage.form.sending')}
                                        </span>
                                    ) : submitted ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <FiCheck size={20} />
                                            {t('contactPage.form.sent')}
                                        </span>
                                    ) : (
                                        <span className="flex items-center justify-center gap-2">
                                            <FiSend size={20} />
                                            {t('contactPage.form.send')}
                                        </span>
                                    )}
                                </button>
                                <p className="text-xs text-center text-gray-500 mt-3 font-medium">
                                    {t('contactPage.form.trustStatement')}
                                </p>
                            </form>
                        </motion.div>

                        {/* Google Map */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="relative flex flex-col"
                        >
                            <div className="h-full min-h-[480px] rounded-2xl overflow-hidden shadow-2xl border border-gray-200 relative">
                                <iframe
                                    src="https://maps.google.com/maps?q=26.2311726,50.586338&hl=en&z=17&output=embed"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    className="w-full h-full object-cover"
                                ></iframe>
                                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/60 to-transparent pointer-events-none"></div>

                                {/* Floating Info Card */}
                                <div className="absolute bottom-6 left-6 right-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
                                    <h4 className="text-lg font-bold text-gray-800 mb-2">{t('contactPage.office.title')}</h4>
                                    <p className="text-gray-600 mb-4 text-sm">{t('contactPage.info.locationValue')}</p>
                                    <a
                                        href="https://www.google.com/maps/place/26%C2%B013'52.2%22N+50%C2%B035'10.8%22E/@26.2311726,50.586338,17z"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm"
                                    >
                                        <FiMapPin />
                                        {t('contactPage.office.directions')} →
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
